import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, Unlock, Lock, Copy, Trash2, ArrowLeftRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const UrlEncoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [encodedOutput, setEncodedOutput] = useState('');
  const [decodedOutput, setDecodedOutput] = useState('');
  const [activeTab, setActiveTab] = useState<'encode' | 'decode'>('encode');

  useSEO({
    title: 'URL Encoder & Decoder - Encode and Decode URLs Online',
    description: 'Free online URL encoder and decoder. Convert URLs to percent-encoded format and decode URL-encoded strings safely.',
    keywords: 'URL encoder, URL decoder, percent encoding, URL escape, URL unescape, encode URL, decode URL online',
    canonical: 'https://snap-tools.online/dev/url-encoder'
  });

  const copyOutput = () => {
    const output = activeTab === 'encode' ? encodedOutput : decodedOutput;
    navigator.clipboard.writeText(output).then(() => {
      toast.success('Output copied to clipboard');
    });
  };

  const clearInputs = () => {
    setInput('');
    setEncodedOutput('');
    setDecodedOutput('');
    toast.success('All fields cleared');
  };

  const loadSample = () => {
    if (activeTab === 'encode') {
      setInput('https://example.com/search?q=hello world&lang=en');
    } else {
      setInput('https%3A%2F%2Fexample.com%2Fsearch%3Fq%3Dhello%20world%26lang%3Den');
    }
    toast.success('Sample URL loaded');
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    if (value.trim()) {
      if (activeTab === 'encode') {
        try {
          const encoded = encodeURIComponent(value);
          setEncodedOutput(encoded);
        } catch (error) {
          setEncodedOutput('');
        }
      } else {
        try {
          const decoded = decodeURIComponent(value);
          setDecodedOutput(decoded);
        } catch (error) {
          setDecodedOutput('');
        }
      }
    } else {
      setEncodedOutput('');
      setDecodedOutput('');
    }
  };

  const switchMode = () => {
    const newTab = activeTab === 'encode' ? 'decode' : 'encode';
    setActiveTab(newTab);
    
    // Auto-fill input with current output for reverse operation
    if (activeTab === 'encode' && encodedOutput) {
      setInput(encodedOutput);
    } else if (activeTab === 'decode' && decodedOutput) {
      setInput(decodedOutput);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Link className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            URL Encoder & Decoder
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Encode URLs to percent-encoded format or decode URL-encoded strings. Real-time conversion 
            with comprehensive error handling and validation.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-6 sm:mb-8"
        >
          <div className="bg-white rounded-xl p-1.5 shadow-lg border border-gray-100">
            <button
              onClick={() => setActiveTab('encode')}
              className={`flex items-center px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg font-semibold transition-all duration-200 text-sm sm:text-base ${
                activeTab === 'encode'
                  ? 'bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#8438ff] hover:bg-gray-50'
              }`}
            >
              <Lock className="h-4 w-4 mr-2" />
              Encode URL
            </button>
            <button
              onClick={() => setActiveTab('decode')}
              className={`flex items-center px-5 sm:px-6 py-3 sm:py-3.5 rounded-lg font-semibold transition-all duration-200 text-sm sm:text-base ${
                activeTab === 'decode'
                  ? 'bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#8438ff] hover:bg-gray-50'
              }`}
            >
              <Unlock className="h-4 w-4 mr-2" />
              Decode URL
            </button>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Input Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Input URL
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={loadSample}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Sample
                  </button>
                  <button
                    onClick={switchMode}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                    title="Switch mode"
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={clearInputs}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Clear all"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>

              <textarea
                value={input}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder={
                  activeTab === 'encode'
                    ? 'Enter URL to encode (e.g., https://example.com/search?q=hello world)'
                    : 'Enter encoded URL to decode (e.g., https%3A%2F%2Fexample.com%2Fsearch%3Fq%3Dhello%20world)'
                }
                className="w-full h-32 sm:h-40 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors font-mono text-xs sm:text-sm"
              />

              <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
                <p className="mb-2">
                  <strong>{activeTab === 'encode' ? 'Encoding' : 'Decoding'}:</strong> 
                  {activeTab === 'encode' 
                    ? ' Converts special characters to percent-encoded format (%20, %3A, etc.)'
                    : ' Converts percent-encoded characters back to original format'
                  }
                </p>
                <p>Characters: {input.length}</p>
              </div>
            </motion.div>
          </div>

          {/* Output Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  {activeTab === 'encode' ? 'Encoded' : 'Decoded'} URL
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  {(activeTab === 'encode' ? encodedOutput : decodedOutput) && (
                    <button
                      onClick={copyOutput}
                      className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>
                  )}
                </div>
              </div>

              <textarea
                value={activeTab === 'encode' ? encodedOutput : decodedOutput}
                readOnly
                className="w-full h-32 sm:h-40 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none bg-gray-50 font-mono text-xs sm:text-sm"
                placeholder={`${activeTab === 'encode' ? 'Encoded' : 'Decoded'} URL will appear here...`}
              />

              {(activeTab === 'encode' ? encodedOutput : decodedOutput) && (
                <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
                  <p>Characters: {(activeTab === 'encode' ? encodedOutput : decodedOutput).length}</p>
                  <p>
                    {activeTab === 'encode' 
                      ? `Compression: ${((1 - encodedOutput.length / Math.max(input.length, 1)) * 100).toFixed(1)}%`
                      : `Expansion: ${((decodedOutput.length / Math.max(input.length, 1) - 1) * 100).toFixed(1)}%`
                    }
                  </p>
                </div>
              )}

              {/* Common URL encoding reference */}
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Common URL Encodings</h4>
                <div className="text-xs text-blue-800 space-y-1">
                  <div className="grid grid-cols-2 gap-2">
                    <span>Space → %20</span>
                    <span>: → %3A</span>
                    <span>/ → %2F</span>
                    <span>? → %3F</span>
                    <span>& → %26</span>
                    <span>= → %3D</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default UrlEncoder;
